// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Container } from '@mui/material';

// Core
import FormValidator from '../core/FormValidator';
import { ParameterValues, paramRepository } from '../core/resources';
import utils from '../core/utils';
import auth from '../core/authorization';

// Common
import ViewLoader from '../common/ViewLoader';
import { ViewTitle } from '../common/typography';
import { ARadioButtons } from '../common/inputs';

// Interfaces
import {
    AnelmaReportTypeEnum,
    AnelmaReportingBtnGroupNameEnum,
    ReportStatus,
} from '../interfaces/enums';

// Store
import { useAppSelector } from '../store/hooks';

// Api
import reportApi, { IGroups } from '../api/reportingApi';
import { ReportingApiUnintercepted } from '../api/reportingApiUnintercepted';

// Feature
import Report from './Report';
import { constructLanguageParamsForQlik } from './helpers';

export interface ReportViewProps {
    title: number;
    btnGroupParameterName: AnelmaReportingBtnGroupNameEnum;
    type: AnelmaReportTypeEnum;
    endQlikSession: (eligible: boolean) => void;
}

export default function ReportView(props: ReportViewProps) {
    const { t } = useTranslation(['AnelmaLayout', 'AnelmaReporting', 'AnelmaGeneral']);

    const qlikUninterceptor = new ReportingApiUnintercepted();

    const culture = useAppSelector((state) => state.configuration.selectedCulture);
    const context = useAppSelector((state) => state.context);
    const qlikConfiguration = useAppSelector((state) => state.configuration.data.qlik);

    const [status, setStatus] = useState<ReportStatus>(ReportStatus.Loading);
    const [crsfToken, setCrsfToken] = useState<string>();
    const [selectedReport, setSelectedReport] = useState<string>('');
    const [reportParameters, setReportParameters] = useState<ParameterValues>([]);
    const [reportUrl, setReportUrl] = useState<string>();
    const [producerNumber, setProducerNumber] = useState<string>('');
    const [farmId, setFarmId] = useState<string>('');

    useEffect(() => {
        initializeParameters();
    }, []);

    useEffect(() => {
        if (qlikConfiguration) {
            qlikUninterceptor.initValues(
                qlikConfiguration?.qlikCloudBaseUrl,
                qlikConfiguration?.webIntegrationId
            );

            reportApi.getToken(composeGroupsPayload()).then((response) => {
                if (!response) {
                    setStatus(ReportStatus.Failed);
                    return;
                }
                qlikUninterceptor.login(response.Entity).then((response2) => {
                    if (!response2) {
                        setStatus(ReportStatus.Failed);
                        return;
                    }
                    qlikUninterceptor.getCrsfToken().then((response3) => {
                        if (!response3) {
                            setStatus(ReportStatus.Failed);
                            return;
                        }
                        setCrsfToken(response3.headers['qlik-csrf-token']);
                        setStatus(ReportStatus.Ready);
                        setSelectedReport('1');
                    });
                });
            });
        }
    }, [qlikConfiguration]);

    // useEffect with callback is same as componentWillUnmount in the prev. React version (when class components where the thing)
    useEffect(() => () => props.endQlikSession(true), []);

    useEffect(() => {
        if (context.data.currentContext) {
            // Take producer number, if failed set status as failed as we cannot proceed
            let producerId = utils.context.parseFarmContext(
                context.data.currentContext.context
            )?.producerId;
            setFarmId(
                utils.context.parseFarmContext(context.data.currentContext.context)?.farmId || ''
            );
            producerId ? setProducerNumber(producerId) : setStatus(ReportStatus.Failed);
            prepareReportUrl();
        }
    }, [context]);

    useEffect(() => {
        if (crsfToken === undefined) {
            setStatus(ReportStatus.Failed);
        }
    }, [crsfToken]);

    // #region Every time selected report or producer number from new context selection changes create report url again
    useEffect(() => {
        prepareReportUrl();
    }, [selectedReport]);

    useEffect(() => {
        if (producerNumber) prepareReportUrl();
    }, [producerNumber]);
    // #endregion

    const initializeParameters = () => {
        paramRepository.load(['AnelmaReporting', 'AnelmaGeneral']).then(() => {
            setReportParameters(
                paramRepository.resource('AnelmaReporting', props.type.toString(), 'default')
            );
        });
    };

    const prepareReportUrl = () => {
        if (
            reportParameters.find((_) => _.code === selectedReport) &&
            reportParameters.find((_) => _.code === selectedReport)?.text !== undefined
        ) {
            let reportUrl: string = t('AnelmaReporting:1014')
                .replace(
                    '{sheet}',
                    String(reportParameters.find((_) => _.code === selectedReport)?.text)
                )
                .replace('{integrationId}', qlikUninterceptor._webIntegrationId ?? '')
                .replace('{language}', constructLanguageParamsForQlik(culture))
                .replace('{farmId}', producerNumber);
            setReportUrl(`${reportUrl}&qlik-csrf-token=${crsfToken}&identity=${farmId}`);
        }
    };

    const composeGroupsPayload = (): IGroups => {
        let groups: IGroups = {};
        if (auth.canViewProductionReport) groups.ProductionReport = String(1048);
        if (auth.canViewHealthReport) groups.HealthReport = String(1047);
        if (auth.canViewFinancialReport) groups.FinancialReport = String(1079);
        if (auth.canViewButcheryReport) groups.ButcheryReport = String(1046);
        if (auth.canViewEconomyTaxYearSummary) groups.TaxYearSummary = String(1081);
        return groups;
    };

    return (
        <Container>
            <ViewTitle>{t(`AnelmaLayout:${props.title}`)}</ViewTitle>
            <ARadioButtons
                id='anelma-report-selection'
                parameterName={`AnelmaReporting:${props.btnGroupParameterName}`}
                onChange={(v) => setSelectedReport(String(v))}
                validator={new FormValidator()}
                value={selectedReport}
                sortValues={'default'}
                withoutContainer
            />
            {status === ReportStatus.Loading && <ViewLoader />}
            {reportUrl !== undefined ? <Report reportUrl={reportUrl} /> : null}
        </Container>
    );
}
